import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { aiqApi, type AiqQuizDomainInfo } from '@/api/aiq'
import { cn } from '@/lib/utils'

const DIFFICULTY_COLORS: Record<number, { bg: string; text: string }> = {
  1: { bg: 'bg-emerald-100', text: 'text-emerald-700' },
  2: { bg: 'bg-sky-100', text: 'text-sky-700' },
  3: { bg: 'bg-amber-100', text: 'text-amber-700' },
  4: { bg: 'bg-orange-100', text: 'text-orange-700' },
  5: { bg: 'bg-rose-100', text: 'text-rose-700' },
}

type DomainFilter = 'all' | 'abstraction' | 'change' | 'relation' | 'multi' | 'none'

function DomainBadge({ domain }: { domain: string }) {
  const colorMap: Record<string, { bg: string; text: string }> = {
    '추상화': { bg: 'bg-purple-100', text: 'text-purple-700' },
    '변화': { bg: 'bg-sky-100', text: 'text-sky-700' },
    '관계': { bg: 'bg-teal-100', text: 'text-teal-700' },
    'Abstraction': { bg: 'bg-purple-100', text: 'text-purple-700' },
    'Change': { bg: 'bg-sky-100', text: 'text-sky-700' },
    'Relation': { bg: 'bg-teal-100', text: 'text-teal-700' },
  }
  const color = colorMap[domain] ?? { bg: 'bg-gray-100', text: 'text-gray-600' }
  return (
    <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium', color.bg, color.text)}>
      {domain}
    </span>
  )
}

// ============================================================================
// Main Component
// ============================================================================

export function AiqDomainAnalysis({ productId }: { productId: number }) {
  const { t } = useTranslation('learner')
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState<DomainFilter>('all')

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['aiqDomainAnalysis', productId],
    queryFn: async () => {
      const res = await aiqApi.getDomainAnalysis(productId)
      return res.data.data
    },
    staleTime: 5 * 60 * 1000,
  })

  const filteredQuizzes = useMemo(() => {
    if (!data) return []
    let quizzes = data.quizzes

    // Apply domain filter
    if (filter === 'abstraction') {
      quizzes = quizzes.filter(q => q.isAbstraction && !q.isChange && !q.isRelation)
    } else if (filter === 'change') {
      quizzes = quizzes.filter(q => !q.isAbstraction && q.isChange && !q.isRelation)
    } else if (filter === 'relation') {
      quizzes = quizzes.filter(q => !q.isAbstraction && !q.isChange && q.isRelation)
    } else if (filter === 'multi') {
      quizzes = quizzes.filter(q => q.domains.length > 1)
    } else if (filter === 'none') {
      quizzes = quizzes.filter(q => q.domains.length === 0)
    }

    // Apply text search
    if (search.trim()) {
      const q = search.trim().toLowerCase()
      quizzes = quizzes.filter(quiz =>
        quiz.quizId.toLowerCase().includes(q) ||
        (quiz.title ?? '').toLowerCase().includes(q) ||
        (quiz.lessonName ?? '').toLowerCase().includes(q) ||
        (quiz.curriculumName ?? '').toLowerCase().includes(q)
      )
    }

    return quizzes
  }, [data, filter, search])

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="text-center">
          <div className="inline-block w-6 h-6 rounded-full border-b-2 border-indigo-600 animate-spin" />
          <p className="mt-2 text-sm text-gray-500">{t('learning.aiqAnalysis.analyzingDomain')}</p>
        </div>
      </div>
    )
  }

  if (!data) {
    return (
      <div className="flex justify-center items-center h-full">
        <p className="text-sm text-gray-500">{t('learning.aiqAnalysis.cannotLoadDomain')}</p>
      </div>
    )
  }

  const { domainStats } = data

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="flex gap-3 justify-between items-center px-6 py-3 border-b border-gray-200 bg-gray-50/50">
        <span className="text-sm font-semibold text-gray-700">{t('learning.aiqAnalysis.domainSettings')}</span>
        <button
          onClick={() => refetch()}
          disabled={isFetching}
          className="flex gap-1 items-center px-2.5 py-1 text-xs font-medium text-indigo-600 bg-indigo-50 rounded-md transition-colors hover:bg-indigo-100 disabled:opacity-50"
        >
          <RefreshCw className={cn('w-3.5 h-3.5', isFetching && 'animate-spin')} />
          {t('learning.aiqAnalysis.refresh')}
        </button>
      </div>

      <div className="overflow-y-auto flex-1 p-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-5 gap-3">
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500">{t('learning.aiqAnalysis.totalAiqQuizzesLabel')}</div>
            <div className="mt-1 text-2xl font-bold text-gray-900">{data.totalAiqQuizzes}</div>
          </div>
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <div className="text-xs text-purple-600">{t('learning.aiqAnalysis.abstraction')}</div>
            <div className="mt-1 text-2xl font-bold text-purple-800">{domainStats.abstractionCount}</div>
          </div>
          <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
            <div className="text-xs text-sky-600">{t('learning.aiqAnalysis.change')}</div>
            <div className="mt-1 text-2xl font-bold text-sky-800">{domainStats.changeCount}</div>
          </div>
          <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
            <div className="text-xs text-teal-600">{t('learning.aiqAnalysis.relation')}</div>
            <div className="mt-1 text-2xl font-bold text-teal-800">{domainStats.relationCount}</div>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="text-xs text-gray-500">{t('learning.aiqAnalysis.noDomainSlashMulti')}</div>
            <div className="mt-1 text-2xl font-bold text-gray-700">
              {domainStats.noDomainCount} / {domainStats.multiDomainCount}
            </div>
          </div>
        </div>

        {/* No Domain Warning */}
        {domainStats.noDomainCount > 0 && (
          <div className="flex gap-2 items-start px-4 py-3 bg-red-50 rounded-lg border border-red-200">
            <AlertTriangle className="flex-shrink-0 mt-0.5 w-4 h-4 text-red-500" />
            <span className="text-sm text-red-700">
              {t('learning.aiqAnalysis.noDomainWarning', { count: domainStats.noDomainCount })}
            </span>
          </div>
        )}

        {/* Domain Combinations */}
        {Object.keys(domainStats.combinations).length > 0 && (
          <div className="p-4 bg-white rounded-lg border border-gray-200">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">{t('learning.aiqAnalysis.domainCombinations')}</h3>
            <div className="flex flex-wrap gap-2">
              {Object.entries(domainStats.combinations).map(([combo, count]) => (
                <span
                  key={combo}
                  className="inline-flex items-center gap-1.5 px-3 py-1 bg-gray-100 rounded-full text-xs text-gray-700"
                >
                  <span className="font-medium">{combo}</span>
                  <span className="px-1.5 py-0.5 bg-white rounded-full text-gray-500 font-semibold">{count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Search + Filter */}
        <div className="flex gap-2 items-center">
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('learning.aiqAnalysis.searchPlaceholder')}
            className="flex-1 px-3 py-1.5 text-sm bg-white rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent"
          />
          <select
            value={filter}
            onChange={e => setFilter(e.target.value as DomainFilter)}
            className="px-3 py-1.5 text-sm bg-white rounded-lg border border-gray-300 outline-none focus:ring-2 focus:ring-indigo-400"
          >
            <option value="all">{t('learning.aiqAnalysis.filterAll')}</option>
            <option value="abstraction">{t('learning.aiqAnalysis.abstraction')}</option>
            <option value="change">{t('learning.aiqAnalysis.change')}</option>
            <option value="relation">{t('learning.aiqAnalysis.relation')}</option>
            <option value="multi">{t('learning.aiqAnalysis.filterMulti')}</option>
            <option value="none">{t('learning.aiqAnalysis.filterNone')}</option>
          </select>
        </div>

        {/* Quiz Table */}
        <div className="rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-y-auto max-h-[500px]">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="text-gray-500 border-b border-gray-200">
                  <th className="px-3 py-2 font-medium text-left w-10">#</th>
                  <th className="px-3 py-2 font-medium text-left w-40">{t('learning.aiqAnalysis.quizId')}</th>
                  <th className="px-3 py-2 font-medium text-left">{t('learning.aiqAnalysis.quizTitle')}</th>
                  <th className="px-3 py-2 font-medium text-center w-16">{t('learning.aiqAnalysis.difficulty')}</th>
                  <th className="px-3 py-2 font-medium text-left w-40">{t('learning.aiqAnalysis.domain')}</th>
                  <th className="px-3 py-2 font-medium text-left w-36">{t('learning.aiqAnalysis.lessonLabel')}</th>
                  <th className="px-3 py-2 font-medium text-left w-36">{t('learning.aiqAnalysis.curriculumLabel')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredQuizzes.map((quiz, idx) => (
                  <QuizRow key={quiz.quizId} quiz={quiz} index={idx + 1} />
                ))}
                {filteredQuizzes.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-gray-400">
                      {t('learning.aiqAnalysis.noAiqQuizzes')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {/* Footer */}
          <div className="px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
            {filteredQuizzes.length < data.totalAiqQuizzes
              ? t('learning.aiqAnalysis.filteredCount', { filtered: filteredQuizzes.length, total: data.totalAiqQuizzes })
              : t('learning.aiqAnalysis.totalCount', { count: data.totalAiqQuizzes })
            }
          </div>
        </div>
      </div>
    </div>
  )
}

function QuizRow({ quiz, index }: { quiz: AiqQuizDomainInfo; index: number }) {
  const hasNoDomain = quiz.domains.length === 0
  const color = DIFFICULTY_COLORS[quiz.difficultyLevel] ?? { bg: 'bg-gray-100', text: 'text-gray-700' }

  return (
    <tr className={cn('border-t border-gray-50 hover:bg-gray-50', hasNoDomain && 'bg-red-50/50')}>
      <td className="px-3 py-1.5 text-gray-400">{index}</td>
      <td className="px-3 py-1.5 font-mono text-gray-600 truncate max-w-[160px]">{quiz.quizId}</td>
      <td className="px-3 py-1.5 text-gray-700 truncate max-w-[200px]">{quiz.title ?? '-'}</td>
      <td className="px-3 py-1.5 text-center">
        <span className={cn('px-1.5 py-0.5 rounded text-[10px] font-medium', color.bg, color.text)}>
          Lv.{quiz.difficultyLevel}
        </span>
      </td>
      <td className="px-3 py-1.5">
        {quiz.domains.length > 0
          ? <div className="flex flex-wrap gap-1">{quiz.domains.map(d => <DomainBadge key={d} domain={d} />)}</div>
          : <span className="text-[10px] text-red-400 font-medium">-</span>
        }
      </td>
      <td className="px-3 py-1.5 text-gray-600 truncate max-w-[144px]">{quiz.lessonName ?? '-'}</td>
      <td className="px-3 py-1.5 text-gray-600 truncate max-w-[144px]">{quiz.curriculumName ?? '-'}</td>
    </tr>
  )
}
